import React, { useState } from 'react';
import { vizAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { BarChart2, Plus, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const CHART_TYPES = [
  { id: 'bar', name: 'Bar Chart' },
  { id: 'line', name: 'Line Chart' },
  { id: 'pie', name: 'Pie Chart' },
  { id: 'table', name: 'Data Table' },
];

const COLORS = ['#1E5A8A', '#1F7A5A', '#B89B5E', '#B3261E', '#9A6B1A', '#5B616E'];

function parseCSVData(csv) {
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim());
  return lines.slice(1).map(line => {
    const vals = line.split(',').map(v => v.trim());
    const obj = {};
    headers.forEach((h, i) => {
      const num = parseFloat(vals[i]);
      obj[h] = isNaN(num) ? vals[i] : num;
    });
    return obj;
  });
}

export function ChartBlockBuilder({ articleId, onInserted }) {
  const [type, setType] = useState('bar');
  const [title, setTitle] = useState('');
  const [csvData, setCsvData] = useState('label,value\nQ1,120\nQ2,240\nQ3,180\nQ4,300');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const data = parseCSVData(csvData);
  const keys = data.length ? Object.keys(data[0]).filter(k => k !== 'label' && k !== 'name') : [];
  const xKey = data.length ? Object.keys(data[0])[0] : 'label';

  const renderPreview = () => {
    if (!data.length) return <p className="text-sm text-[#5B616E] text-center py-4">No data</p>;
    if (type === 'table') {
      const cols = Object.keys(data[0]);
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr>{cols.map(c => <th key={c} className="bg-[#121316] text-white px-3 py-2 text-left">{c}</th>)}</tr></thead>
            <tbody>{data.map((row, i) => <tr key={i} className="border-b border-[#E7E0D6]">{cols.map(c => <td key={c} className="px-3 py-2">{String(row[c])}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={180}>
        {type === 'pie' ? (
          <PieChart>
            <Pie data={data} dataKey={keys[0] || 'value'} nameKey={xKey} cx="50%" cy="50%" outerRadius={70}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        ) : type === 'line' ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            {keys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} />)}
          </LineChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            {keys.map((k, i) => <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />)}
          </BarChart>
        )}
      </ResponsiveContainer>
    );
  };

  const handleInsert = async () => {
    if (!title.trim()) { toast.error('Chart title required'); return; }
    if (!data.length) { toast.error('Add some data (CSV format)'); return; }
    setSaving(true);
    try {
      const r = await vizAPI.create({
        title,
        type,
        data: { rows: data, keys, xKey },
        article_id: articleId,
      });
      toast.success('Chart saved and linked to article');
      onInserted?.({ viz_id: r.data.id, title, type });
    } catch { toast.error('Failed to save chart'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-[#E7E0D6] bg-white">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-[#B89B5E]" /> Interactive Chart Builder
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-[#5B616E] block mb-0.5">Chart type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHART_TYPES.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#5B616E] block mb-0.5">Title</label>
            <Input className="h-8 text-xs" value={title} onChange={e => setTitle(e.target.value)} placeholder="Chart title..." />
          </div>
        </div>

        <div>
          <label className="text-xs text-[#5B616E] block mb-0.5">Data (CSV — first column = X axis / labels)</label>
          <Textarea rows={6} className="font-mono text-xs" value={csvData} onChange={e => setCsvData(e.target.value)} placeholder="label,value&#10;Q1,120" />
        </div>

        {showPreview && (
          <div className="border border-[#E7E0D6] rounded-xl p-3 bg-[#FBF7EF]">
            {title && <p className="text-xs font-semibold mb-2 text-center">{title}</p>}
            {renderPreview()}
          </div>
        )}

        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowPreview(v => !v)}>
            {showPreview ? <X className="w-3 h-3 mr-1" /> : <BarChart2 className="w-3 h-3 mr-1" />}
            {showPreview ? 'Hide preview' : 'Preview'}
          </Button>
          <Button size="sm" onClick={handleInsert} disabled={saving} className="flex-1 text-xs bg-[#121316] text-white hover:bg-[#1B1D22]">
            <Check className="w-3 h-3 mr-1" /> {saving ? 'Saving…' : 'Insert Chart'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
